import React, { useEffect, useState } from 'react';
import { AssetHandler, useAssets } from './components/AssetHandler';
import { SoundHandler } from './components/SoundHandler';
import { VerifyGameRunner } from './components/VerifyGameRunner';
import { GRAPHQL_URL } from '~/core/constants';
import { api, BlinkoGame, checkVersionMode } from './api';
import Loader from '~/core/components/Loader';
import { formatEther } from 'viem';

interface VerifyListItem {
  id: string;
  gameId: string;
  betAmount: string;
  gameSeed: string;
  gameConfig: string;
  totalWin?: string;
  status: 'pending' | 'verified' | 'failed';
  details?: string;
  isHardMode: boolean;
  serverRtp: number;
  clientRtp?: number;
}

// Helper to calculate RTP
const calculateRtp = (winWei: string, betWei: string) => {
  const w = Number(formatEther(BigInt(winWei || '0')));
  const b = Number(formatEther(BigInt(betWei || '0')));
  return b > 0 ? (w / b) * 100 : 0;
};

const VerifyPage: React.FC = () => {
  // --- State ---
  // Search / Fetch
  const [strategy, setStrategy] = useState<'recent' | 'high_rollers' | 'big_wins'>('recent');
  const [manualId, setManualId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Data
  const [list, setList] = useState<VerifyListItem[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [activeGameFull, setActiveGameFull] = useState<BlinkoGame | null>(null);

  const { markAssetsReady } = useAssets();
  useEffect(() => markAssetsReady(), []);

  // --- Fetch Logic ---
  const fetchGames = async () => {
    setIsLoading(true);
    let orderBy = 'resolvedAt';
    const orderDirection = 'desc';
    const whereCondition = 'status: Won'; // Keep interesting

    if (strategy === 'high_rollers') orderBy = 'betAmount';
    else if (strategy === 'big_wins') orderBy = 'payoutAmount';

    const query = `
      query {
        games(first: 10, where: { ${whereCondition} }, orderBy: ${orderBy}, orderDirection: ${orderDirection}) {
          id
        }
      }
    `;

    try {
      const res = await fetch(GRAPHQL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      const json = await res.json();
      const fetched = json.data?.games || [];

      // Fetch full details from API for each game to get gameConfig, seeds, etc.
      const detailedGames = await Promise.all(
        fetched.map(async (g: { id: string }) => {
            try {
                return await api.getGame(g.id);
            } catch (e) {
                console.error(`Failed to load details for ${g.id}`, e);
                return null;
            }
        })
      );

      const validGames = detailedGames.filter((g): g is BlinkoGame => g !== null);

      // Transform and prepend to list (avoid duplicates)
      const newItems: VerifyListItem[] = validGames.map((g) => ({
        id: g.gameId,
        gameId: g.gameId,
        betAmount: g.betAmount,
        gameSeed: g.gameSeed,
        gameConfig: g.gameConfig,
        totalWin: g.gameState?.totalWin || g.payoutAmount || '0',
        status: 'pending',
        isHardMode: checkVersionMode(g).isHardMode,
        serverRtp: calculateRtp(g.gameState?.totalWin || g.payoutAmount || '0', g.betAmount),
      }));

      setList(prev => {
        const existingIds = new Set(prev.map(i => i.gameId));
        const filtered = newItems.filter(i => !existingIds.has(i.gameId));
        return [...filtered, ...prev];
      });

      // If list was empty, select first new one
      if (list.length === 0 && newItems.length > 0) {
        setSelectedGameId(newItems[0].gameId);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const addManualGame = async () => {
    if (!manualId) return;
    setIsLoading(true);
    try {
      const g = await api.getGame(manualId);
      const newItem: VerifyListItem = {
        id: g.gameId,
        gameId: g.gameId,
        betAmount: g.betAmount,
        gameSeed: g.gameSeed,
        gameConfig: g.gameConfig,
        totalWin: g.gameState?.totalWin || g.payoutAmount || '0',
        status: 'pending',
        isHardMode: checkVersionMode(g).isHardMode,
        serverRtp: calculateRtp(g.gameState?.totalWin || '0', g.betAmount),
      };
      setList(prev => [newItem, ...prev.filter(i => i.gameId !== g.gameId)]);
      setSelectedGameId(g.gameId);
      setManualId('');
    } catch (e) {
      alert("Game not found or error loading");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Selection Logic ---
  useEffect(() => {
    if (selectedGameId) {
      // Load full game data if not already fully loaded or if switching
      api.getGame(selectedGameId).then(setActiveGameFull).catch(console.error);
    } else {
      setActiveGameFull(null);
    }
  }, [selectedGameId]);

  // --- Verification Callback ---
  const handleVerificationComplete = (gameId: string, success: boolean, clientWinWei: bigint, msg?: string) => {
    setList(prev => prev.map(item => {
      if (item.gameId !== gameId) return item;
      return {
        ...item,
        status: success ? 'verified' : 'failed',
        details: msg,
        clientRtp: calculateRtp(clientWinWei.toString(), item.betAmount),
      };
    }));
  };

  const copyToClipboard = (text: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
  };

  return (
    <AssetHandler>
      <SoundHandler />
      <div className="fixed inset-0 bg-black flex flex-row overflow-hidden font-sans text-neutral-200 selection:bg-white/20">
        
        {/* --- Main Area --- */}
        <div className="absolute inset-0 z-0">
           {activeGameFull ? (
               <VerifyGameRunner 
                   key={activeGameFull.gameId} // Force reset on ID change
                   game={activeGameFull}
                   physicsSteps={10} // Hardcoded to Fast
                   onComplete={() => {
                        // handled by withStats
                   }}
                   onCompleteWithStats={(success, winEth, msg) => {
                        handleVerificationComplete(activeGameFull.gameId, success, winEth, msg);
                   }}
               />
           ) : (
               <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-700 pointer-events-none pb-20 pr-[420px]"> {/* Visual centering adjustment */}
                   <div className="w-12 h-12 rounded-full border border-neutral-800 mb-4 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-neutral-700 rounded-full" />
                   </div>
                   <div className="font-sans font-medium text-sm tracking-widest uppercase">Select a game</div>
               </div>
           )}
        </div>

        {/* --- Sidebar --- */}
        <div className="absolute right-0 top-0 bottom-0 z-40 w-[400px] flex flex-col border-l border-white/5 bg-neutral-950/95 backdrop-blur-xl shadow-2xl">
          
          {/* Header */}
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between bg-neutral-900/50">
            <h1 className="font-bold text-xs tracking-widest text-white flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
              VERIFY
            </h1>
            <div className="text-[9px] uppercase font-bold tracking-widest text-neutral-600">
                v0.1
            </div>
          </div>

          {/* Search / Fetch Section */}
          <div className="p-4 border-b border-white/5 space-y-3 bg-neutral-900/20">
             <div className="flex gap-2">
                <input 
                  value={manualId}
                  onChange={e => setManualId(e.target.value)}
                  placeholder="Enter Game ID..."
                  className="flex-1 bg-white/5 border border-white/5 rounded px-3 py-1.5 text-[11px] text-white placeholder-neutral-600 focus:border-white/20 focus:bg-white/10 focus:outline-none transition-all"
                />
                <button 
                  onClick={addManualGame}
                  className="px-3 bg-white text-black hover:bg-neutral-200 rounded text-[10px] font-bold uppercase tracking-wider transition-colors shadow-lg shadow-white/5"
                >
                  Add
                </button>
             </div>
             <div className="flex gap-2">
                <div className="relative flex-1">
                    <select
                        value={strategy}
                        onChange={e => setStrategy(e.target.value as any)}
                        className="w-full appearance-none bg-white/5 border border-white/5 rounded px-3 py-1.5 text-[11px] text-white focus:border-white/20 focus:bg-white/10 outline-none cursor-pointer transition-all"
                    >
                        <option value="recent">Recent Wins</option>
                        <option value="high_rollers">High Rollers</option>
                        <option value="big_wins">Big Wins</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500">
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                    </div>
                </div>
                <button 
                  onClick={fetchGames}
                  disabled={isLoading}
                  className="px-3 bg-white/5 hover:bg-white/10 border border-white/5 text-neutral-200 disabled:opacity-50 rounded text-[10px] font-bold uppercase tracking-wider transition-colors"
                >
                  {isLoading ? '...' : 'Fetch'}
                </button>
             </div>
          </div>

          {/* List Section */}
          <div className="flex-1 overflow-y-auto min-h-0 bg-transparent custom-scrollbar">
             <div className="sticky top-0 bg-neutral-950/80 backdrop-blur-md z-10 px-5 py-2 border-b border-white/5 flex justify-between items-center">
                <span className="text-[9px] font-bold text-neutral-500 uppercase tracking-widest">Queue ({list.length})</span>
                {list.length > 0 && (
                    <button onClick={() => setList([])} className="text-[9px] font-medium text-neutral-600 hover:text-red-400 transition-colors uppercase tracking-wider group flex items-center gap-1">
                        Clear
                    </button>
                )}
             </div>
             <div className="divide-y divide-white/[0.03]">
                {isLoading && (
                    <div className="p-12 flex justify-center">
                        <Loader size="16px" stroke="#444" />
                    </div>
                )}
                {!isLoading && list.length === 0 && (
                    <div className="flex flex-col items-center justify-center p-16 text-neutral-700 space-y-3">
                         <div className="text-[10px] font-medium uppercase tracking-widest text-neutral-800">No games</div>
                    </div>
                )}
                {list.map(item => (
                    <div 
                        key={item.gameId}
                        onClick={() => setSelectedGameId(item.gameId)}
                        className={`group relative px-5 py-3 cursor-pointer transition-all border-l-[3px] ${
                            selectedGameId === item.gameId 
                                ? 'bg-white/[0.03] border-emerald-500' 
                                : 'bg-transparent border-transparent hover:bg-white/[0.02] hover:border-neutral-800'
                        }`}
                    >
                        {/* Status Dot (Absolute) */}
                        <div className={`absolute top-4 left-2 w-1 h-1 rounded-full ${
                             item.status === 'verified' ? 'bg-emerald-500' :
                             item.status === 'failed' ? 'bg-red-500' :
                             'bg-neutral-700'
                        }`} />

                        {/* Top Row: ID & Hard Mode */}
                        <div className="flex justify-between items-center mb-2">
                             <div className="font-mono text-[10px] text-neutral-300 flex items-center gap-2 group/id">
                                 <span className={selectedGameId === item.gameId ? 'text-white font-bold' : ''}>{item.gameId.slice(0, 8)}...{item.gameId.slice(-4)}</span>
                                 <button 
                                    onClick={(e) => copyToClipboard(item.gameId, e)}
                                    className="opacity-0 group-hover/id:opacity-100 text-neutral-600 hover:text-white transition-all"
                                >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                </button>
                             </div>
                             {item.isHardMode && (
                                 <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-500 border border-red-500/20 uppercase tracking-wider">
                                     HARD
                                 </span>
                             )}
                        </div>

                        {/* Middle Row: Metrics Grid */}
                        <div className="grid grid-cols-2 gap-y-1 gap-x-4 mb-2">
                            <div className="flex flex-col gap-0.5">
                                <span className="text-[8px] font-bold text-neutral-600 uppercase tracking-wider">Bet</span>
                                <span className="text-[10px] font-mono text-neutral-300">{Number(formatEther(BigInt(item.betAmount))).toFixed(3)} <span className="text-neutral-600">ETH</span></span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-[8px] font-bold text-neutral-600 uppercase tracking-wider">RTP</span>
                                <div className="text-[10px] font-mono flex items-center gap-1">
                                    <span className={item.clientRtp !== undefined ? (item.clientRtp >= 100 ? 'text-emerald-400' : 'text-neutral-300') : 'text-neutral-600'}>
                                        {item.clientRtp !== undefined ? `${item.clientRtp.toFixed(0)}` : '--'}
                                    </span>
                                    <span className="text-neutral-700">/</span>
                                    <span className={item.serverRtp >= 100 ? 'text-emerald-500' : 'text-neutral-500'}>
                                        {item.serverRtp.toFixed(0)}%
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Bottom Row: Actions or Status */}
                        <div className="flex items-center justify-between min-h-[20px]">
                            {/* Left: Seed (Hover only) */}
                            <div className="text-[9px] text-neutral-600 font-mono opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                                <span>Seed: {item.gameSeed.slice(0, 6)}...</span>
                            </div>

                            {/* Right: Verification Status / Button */}
                            <div className="ml-auto">
                                {item.status === 'pending' ? (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); setSelectedGameId(item.gameId); }}
                                        className="text-[9px] bg-emerald-500/10 hover:bg-emerald-500 text-emerald-500 hover:text-black px-3 py-0.5 rounded-full font-bold uppercase tracking-wider transition-all border border-emerald-500/20 hover:border-emerald-500"
                                    >
                                        Select
                                    </button>
                                ) : item.status === 'failed' ? (
                                    <span className="text-[9px] text-red-500 font-bold uppercase tracking-wider flex items-center gap-1">
                                        Failed
                                    </span>
                                ) : (
                                    <span className="text-[9px] text-emerald-500 font-bold uppercase tracking-wider flex items-center gap-1 bg-emerald-500/5 px-2 py-0.5 rounded-full border border-emerald-500/10">
                                        Verified
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {/* Status Footer */}
            {selectedGameId && list.find(l => l.gameId === selectedGameId)?.status === 'failed' && (
                <div className="p-3 bg-red-900/10 border-t border-red-900/20 text-red-400 text-[10px] font-mono break-all sticky bottom-0 backdrop-blur-md">
                     Error: {list.find(l => l.gameId === selectedGameId)?.details}
                </div>
            )}
          </div>
        </div>
      </div>
    </AssetHandler>
  );
};


export default VerifyPage;

